import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Box, Typography, Stack, Grid, Chip, Button, IconButton, Tooltip, Alert, LinearProgress,
    Snackbar
} from '@mui/material';
import SavingsRoundedIcon from '@mui/icons-material/SavingsRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import { useSelector } from 'react-redux';
import http, { apiErrorMessage } from '../../Api/http';
import { RequestAdvance, GetMyAdvanceRequests } from '../../Api/Api';
import { selectEmployees } from '../../redux/slices/employeesSlice';
import { PRIMARY, PRIMARY_LIGHT } from '../../theme';
import { card, solidBtn, Panel, EmptyState, SkeletonRows, LoadAlert } from '../uiKit';
import RequestAdvanceDialog from './RequestAdvanceDialog';

const inr = (v) => `₹${(Number(v) || 0).toLocaleString('en-IN')}`;

const STATUS_TONE = {
    active: { c: '#0891B2', bg: '#E0F2FE' },
    pending: { c: '#B45309', bg: '#FEF3C7' },
    approved: { c: '#16A34A', bg: '#DCFCE7' },
    completed: { c: '#16A34A', bg: '#DCFCE7' },
    rejected: { c: '#DC2626', bg: '#FEE2E2' },
};
const toneFor = (s) => STATUS_TONE[String(s || '').toLowerCase()] || { c: '#64748B', bg: '#F1F5F9' };

const monthLabel = (v) => {
    const m = /^(\d{4})-(\d{2})$/.exec(String(v || ''));
    if (!m) return v || '';
    return new Date(Number(m[1]), Number(m[2]) - 1, 1).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
};
const n = (v) => Number(v) || 0;

export default function AdvanceRequestScreen() {
    const auth = useSelector((state) => state.auth);
    const employees = useSelector(selectEmployees);
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [open, setOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [snack, setSnack] = useState(null);

    const notify = (msg, sev = 'success') => setSnack({ msg, sev });

    const me = useMemo(() => {
        const code = auth?.user?.employeeCode || auth?.employeeCode || auth?.rollNumber || '';
        const record = code ? employees.find((e) => e.employeeId === code) : null;
        return {
            employeeId: code,
            employeeName: record ? `${record.firstName} ${record.lastName}` : (auth?.user?.name || auth?.userName || ''),
            department: record?.department || auth?.user?.department || '',
        };
    }, [auth, employees]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data: body } = await http.get(GetMyAdvanceRequests);
            if (body?.error) throw new Error(body.message || 'Could not load your advances.');
            const d = body?.data;
            setRows(Array.isArray(d) ? d : (d?.requests || d?.items || d?.advances || []));
            setLoadError('');
        } catch (err) {
            if (err?.response?.status === 404) { setRows([]); setLoadError(''); }
            else setLoadError(apiErrorMessage(err, 'Could not load your advances.'));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const submit = async ({ amount, plan, months, startMonth, reason }) => {
        setSaving(true);
        try {
            const { data: body } = await http.post(RequestAdvance, {
                amount,
                recoveryPlan: plan === 'onetime' ? 'onetime' : 'monthly',
                months: plan === 'onetime' ? 1 : months,
                startMonth,
                notes: reason,
            });
            if (body?.error) throw new Error(body.message || 'Could not raise the request.');
            notify(body?.message || 'Advance request raised.');
            setOpen(false);
            await load();
        } catch (err) {
            notify(apiErrorMessage(err, 'Could not raise the request.'), 'error');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Box sx={{ p: 2, pt: 1.5 }}>
            <Panel
                title="My Salary Advances"
                icon={SavingsRoundedIcon}
                chip={`${rows.length}`}
                action={(
                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                        <Tooltip arrow title="Reload">
                            <IconButton onClick={load} disabled={loading} sx={{ border: '1px solid #E6EAF1', borderRadius: '7px', color: '#64748B', height: 38, width: 38, '&:hover': { bgcolor: PRIMARY_LIGHT, color: PRIMARY } }}>
                                <RefreshRoundedIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                        </Tooltip>
                        <Button startIcon={<AddRoundedIcon />} onClick={() => setOpen(true)} sx={{ ...solidBtn, height: 38, px: 1.8, fontSize: 13 }}>Request Advance</Button>
                    </Stack>
                )}
            >
                {loading ? (
                    <SkeletonRows count={5} />
                ) : loadError ? (
                    <Box sx={{ p: 3 }}>
                        <LoadAlert sx={{ borderRadius: '9px' }} action={<Button size="small" onClick={load} sx={{ textTransform: 'none', fontWeight: 700 }}>Retry</Button>}>{loadError}</LoadAlert>
                    </Box>
                ) : rows.length === 0 ? (
                    <EmptyState
                        icon={SavingsRoundedIcon}
                        title="No advance requests yet"
                        hint="Request a salary advance and choose how many months to recover it over."
                    />
                ) : (
                    <Box sx={{ p: 1.8 }}>
                        <Grid container spacing={1.2}>
                            {rows.map((r, idx) => {
                                const tone = toneFor(r.status);
                                const amount = n(r.amount);
                                const recovered = n(r.recoveredAmount ?? r.recovered);
                                const outstanding = r.outstanding != null ? n(r.outstanding) : Math.max(0, amount - recovered);
                                const pct = amount ? Math.min(100, Math.round((recovered / amount) * 100)) : 0;
                                const showProgress = ['active', 'completed'].includes(String(r.status).toLowerCase());
                                return (
                                    <Grid size={{ xs: 12, sm: 6, lg: 4 }} key={r.id ?? idx}>
                                        <Box sx={{ ...card, p: 1.8, height: '100%', display: 'flex', flexDirection: 'column', gap: 1 }}>
                                            <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
                                                <Typography sx={{ fontSize: 18, fontWeight: 800, color: '#0F172A' }}>{inr(amount)}</Typography>
                                                <Chip label={r.status} size="small" sx={{ height: 22, fontSize: 10.5, fontWeight: 800, bgcolor: tone.bg, color: tone.c }} />
                                            </Stack>
                                            <Typography sx={{ fontSize: 12, color: '#64748B' }}>
                                                {String(r.recoveryPlan).toLowerCase() === 'onetime'
                                                    ? 'One-time deduction'
                                                    : `${n(r.months) || '—'} monthly instalment${n(r.months) === 1 ? '' : 's'}${r.deductPerMonth ? ` · ${inr(r.deductPerMonth)}/mo` : (r.months ? ` · ${inr(Math.round(amount / n(r.months)))}/mo` : '')}`}
                                                {r.startMonth ? ` · from ${monthLabel(r.startMonth)}` : ''}
                                            </Typography>
                                            {showProgress && (
                                                <Box>
                                                    <Stack direction="row" sx={{ justifyContent: 'space-between', mb: 0.4 }}>
                                                        <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#16A34A' }}>{inr(recovered)} recovered</Typography>
                                                        <Typography sx={{ fontSize: 11, fontWeight: 700, color: outstanding ? PRIMARY : '#16A34A' }}>{outstanding ? `${inr(outstanding)} left` : 'Cleared'}</Typography>
                                                    </Stack>
                                                    <LinearProgress variant="determinate" value={pct} sx={{ height: 6, borderRadius: 5, bgcolor: '#EEF0F6', '& .MuiLinearProgress-bar': { bgcolor: pct === 100 ? '#16A34A' : PRIMARY, borderRadius: 5 } }} />
                                                </Box>
                                            )}
                                            {r.rejectReason && (
                                                <Typography sx={{ fontSize: 11.5, color: '#B91C1C', bgcolor: '#FEF2F2', border: '1px solid #FEE2E2', borderRadius: '7px', px: 1, py: 0.6 }}>Rejected: {r.rejectReason}</Typography>
                                            )}
                                            {r.notes && !r.rejectReason && <Typography sx={{ fontSize: 12, color: '#64748B', lineHeight: 1.5 }}>{r.notes}</Typography>}
                                        </Box>
                                    </Grid>
                                );
                            })}
                        </Grid>
                    </Box>
                )}
            </Panel>

            <RequestAdvanceDialog
                open={open}
                onClose={() => setOpen(false)}
                me={me}
                saving={saving}
                onSubmit={submit}
                onInvalid={() => notify('Please complete the highlighted fields.', 'warning')}
            />

            <Snackbar open={Boolean(snack)} autoHideDuration={3400} onClose={() => setSnack(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
                <Alert onClose={() => setSnack(null)} severity={snack?.sev || 'success'} variant="filled" sx={{ borderRadius: '7px' }}>{snack?.msg}</Alert>
            </Snackbar>
        </Box>
    );
}
